import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMe } from './api/hooks';
import { ForcedPasswordChange } from './components/ChangePassword';
import { ApiError, errorMessage } from './api/client';
import { Shell } from './components/Shell';
import { IdleGuard } from './components/IdleGuard';
import { Login } from './pages/Login';
import { ForgotPassword, ResetPassword } from './pages/ResetPassword';
import { SetupWizard, useSetup } from './pages/Setup';
import { ForcedMfaEnrolment } from './components/Mfa';
import { Home } from './pages/Home';
import { Library } from './pages/Library';
import { AssetPage } from './pages/Asset';
import { Collections } from './pages/Collections';
import { Monitoring } from './pages/Monitoring';
import { Tasks } from './pages/Tasks';
import { RecycleBin } from './pages/RecycleBin';
import { Admin } from './pages/admin/Admin';
import { Help } from './pages/help/Help';
import { Mobile } from './mobile/Mobile';
import { isPhone, useDesktopPreference } from './mobile/device';

/** On a phone, the library opens the mobile layout unless the person chose the desktop version. */
function PhoneAware({ children }: { children: React.ReactNode }) {
  const [desktop] = useDesktopPreference();
  if (isPhone() && !desktop) return <Navigate to="/m" replace />;
  return <>{children}</>;
}
import { CreateHome } from './pages/create/CreateHome';
import { EditorPage } from './pages/create/Editor';
import { Workflows } from './pages/workflows/Workflows';
import { Graphics } from './pages/graphics/Graphics';
import { Intake } from './pages/intake/Intake';
import { GroupPage } from './pages/groups/GroupPage';

export function App() {
  const me = useMe();
  const location = useLocation();
  // First-run setup (1.0.0-rc.4): administrators are led through it before anything else while it is waiting.
  const isAdmin = Boolean(me.data?.permissions.includes('admin')) && !me.data?.mustChangePassword && !me.data?.mfaEnrolmentRequired;
  const setup = useSetup(isAdmin);
  if (me.isLoading || (isAdmin && setup.isLoading)) {
    return <div className="empty" style={{ height: '100vh' }}><Loader2 className="spin" color="var(--accent)" /></div>;
  }
  const unauthenticated = me.isError && (me.error as ApiError).status === 401;
  if (me.isError && !unauthenticated) {
    return (
      <div className="empty" style={{ height: '100vh' }}><div className="empty-inner">
        <h2>PRISM is not reachable</h2>
        <p>{errorMessage(me.error)}</p>
        <button className="btn" onClick={() => void me.refetch()}>Try again</button>
      </div></div>
    );
  }
  if (unauthenticated || !me.data) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }
  if (me.data.mustChangePassword) return <ForcedPasswordChange me={me.data} />;
  // Two-step sign-in required and not set up (1.0.0-rc.4): its setup, before anything else.
  if (me.data.mfaEnrolmentRequired) return <ForcedMfaEnrolment me={me.data} />;
  if (location.pathname === '/reset-password') return <ResetPassword />;
  if (isAdmin && (setup.data?.needed || location.pathname === '/setup')) return <SetupWizard me={me.data} />;
  return (
    <>
    {me.data.session && <IdleGuard idleMinutes={me.data.session.idleMinutes} />}
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/m/*" element={<Mobile />} />
      <Route element={<Shell me={me.data} />}>
        <Route path="/" element={<PhoneAware><Home /></PhoneAware>} />
        <Route path="/library" element={<PhoneAware><Library /></PhoneAware>} />
        <Route path="/assets/:id" element={<AssetPage />} />
        <Route path="/recycle-bin" element={<RecycleBin />} />
        <Route path="/collections" element={<Collections />} />
        {/* 1.4.0 (G2): a group's page (a series, a season). */}
        <Route path="/groups/:id" element={<GroupPage />} />
        <Route path="/monitor" element={<Monitoring />} />
        <Route path="/intake/*" element={<Intake />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/create" element={<CreateHome />} />
        <Route path="/create/:id" element={<EditorPage />} />
        <Route path="/graphics/*" element={<Graphics />} />
        <Route path="/workflows/*" element={<Workflows />} />
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/help/*" element={<Help />} />
        <Route path="*" element={<Navigate to="/library" replace />} />
      </Route>
    </Routes>
    </>
  );
}
